import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../../../config/firebase/config';
import { useRestaurant } from '../../../providers/RestaurantContext';
import { ArrowLeft } from 'lucide-react';

interface TransactionDoc {
  id: string;
  mesaId: string;
  numeroMesa?: string;
  posOrderId?: string | null;
  subtotal?: number;
  total?: number;
  paymentMethod?: string;
  processor?: string;
  status?: string;
  approved?: boolean;
  createdAt?: { toDate: () => Date };
}

export default function MesaTransactionsScreen() {
  const { id: mesaId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { restaurant } = useRestaurant();
  const [transactions, setTransactions] = useState<TransactionDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [ticketTotal, setTicketTotal] = useState<number | null>(null);
  const [ticketRestante, setTicketRestante] = useState<number | null>(null);

  useEffect(() => {
    if (!mesaId || !restaurant?.id) return;
    const ref = collection(db, 'restaurants', restaurant.id, 'transactions');
    const q = query(ref, where('mesaId', '==', mesaId));
    getDocs(q)
      .then((snap) => {
        const list = snap.docs
          .map((doc) => {
            const d = doc.data();
            return {
              id: doc.id,
              ...d,
              createdAt: d.createdAt,
            } as TransactionDoc;
          })
          .sort((a, b) => {
            const at = a.createdAt?.toDate?.()?.getTime() ?? 0;
            const bt = b.createdAt?.toDate?.()?.getTime() ?? 0;
            return bt - at;
          });
        setTransactions(list);
      })
      .catch((err) => console.error('Error loading transactions:', err))
      .finally(() => setLoading(false));
  }, [mesaId, restaurant?.id]);

  useEffect(() => {
    if (!mesaId || !restaurant?.id) return;
    const mesaRef = doc(db, 'restaurants', restaurant.id, 'mesas', mesaId);
    getDoc(mesaRef)
      .then((mesaSnap) => {
        if (!mesaSnap.exists()) return;
        const mesa = mesaSnap.data();
        const activeSessionId = mesa?.activeSessionId;
        if (typeof activeSessionId !== 'string') {
          const total = mesa?.montoTotalConImpuestos ?? null;
          const restante = mesa?.restante ?? null;
          setTicketTotal(total ?? null);
          setTicketRestante(restante ?? null);
          return;
        }
        const sessionRef = doc(mesaRef, 'sessions', activeSessionId);
        return getDoc(sessionRef).then((sessionSnap) => {
          if (!sessionSnap.exists()) return;
          const s = sessionSnap.data();
          setTicketTotal(s?.montoTotalConImpuestos ?? s?.montoTotal ?? null);
          setTicketRestante(s?.restante ?? null);
        });
      })
      .catch((err) => console.error('Error loading mesa/session:', err));
  }, [mesaId, restaurant?.id]);

  const formatCurrency = (n: number) =>
    new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n ?? 0);

  const hasTransactions = transactions.length > 0;
  const primaryTx = hasTransactions ? transactions[0] : null;

  const mesaLabel =
    primaryTx?.numeroMesa ??
    primaryTx?.mesaId ??
    mesaId ??
    '';

  const ticketId = primaryTx?.posOrderId ?? '—';

  const approvedTransactions = transactions.filter((tx) => tx.approved);
  const totalPagado = approvedTransactions.reduce(
    (sum, tx) => sum + (tx.total ?? tx.subtotal ?? 0),
    0
  );
  const pagosRealizados = approvedTransactions.length;
  const restanteValue = ticketRestante ?? 0;
  const dominantRestante = restanteValue > 0;
  const ticketShortId = ticketId !== '—' && ticketId.length >= 8 ? ticketId.slice(0, 8) : ticketId;

  const formatStatus = (tx: TransactionDoc) => {
    if (tx.status === 'refunded') return 'Pago reembolsado';
    if (tx.approved) return 'Pago aprobado';
    return 'Pago fallido';
  };

  const statusStyle = (tx: TransactionDoc) => {
    if (tx.status === 'refunded') {
      return 'bg-amber-500/10 text-amber-300 border-amber-400/30';
    }
    if (tx.approved) {
      return 'bg-green-500/10 text-green-400 border-green-500/30';
    }
    return 'bg-red-500/10 text-red-300 border-red-500/30';
  };

  const amountColor = (tx: TransactionDoc) => {
    if (tx.status === 'refunded') return 'text-amber-300';
    if (tx.approved) return 'text-green-400';
    return 'text-red-300';
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-lg hover:bg-zinc-900/60"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-lg font-semibold tracking-tight">Transacciones de la mesa</h1>
        </div>

        {loading ? (
          <p className="text-sm text-zinc-400">Cargando…</p>
        ) : !hasTransactions ? (
          <p className="text-sm text-zinc-400">No hay transacciones para esta mesa.</p>
        ) : (
          <>
            {/* Ticket Summary Card — premium two-level, vertical stack, one dominant number */}
            <div className="mb-6 bg-white/5 border border-white/10 rounded-xl p-6 space-y-6">
              <div>
                <p className="text-xs text-white/50">Mesa</p>
                <p className="text-lg font-semibold text-white">
                  Mesa {mesaLabel || '—'}
                </p>
                <p className="text-xs text-white/40 mt-1 font-mono">
                  Ticket #{ticketShortId}
                </p>
              </div>

              <div className="border-t border-white/10 pt-6">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                  <div>
                    <p className="text-xs text-white/50">Total de la cuenta</p>
                    <p className="text-lg font-semibold text-white">
                      RD${formatCurrency(ticketTotal ?? totalPagado)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-white/50">Pagado</p>
                    <p
                      className={
                        dominantRestante
                          ? 'text-lg font-semibold text-green-400'
                          : 'text-2xl font-bold text-green-400'
                      }
                    >
                      RD${formatCurrency(totalPagado)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-white/50">Restante</p>
                    <p
                      className={
                        dominantRestante
                          ? 'text-2xl font-bold text-red-400'
                          : 'text-lg font-semibold text-red-400'
                      }
                    >
                      RD${formatCurrency(restanteValue)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="border-t border-white/10 pt-6 text-sm text-white/60">
                Pagos realizados: {pagosRealizados}
              </div>
            </div>

            {/* Transactions Section */}
            <div>
              <h2 className="text-sm font-medium text-white/60 mb-3">
                Transacciones
              </h2>
              <ul className="space-y-4">
                {transactions.map((tx) => {
                  const amount = tx.total ?? tx.subtotal ?? 0;
                  const created =
                    tx.createdAt && typeof tx.createdAt.toDate === 'function'
                      ? tx.createdAt.toDate()
                      : null;
                  return (
                    <li
                      key={tx.id}
                      className="bg-white/5 border border-white/10 rounded-lg p-4 flex justify-between items-start gap-4"
                    >
                      <div className="space-y-1">
                        <span
                          className={`inline-flex items-center px-2 py-1 rounded-md text-[11px] font-medium border ${statusStyle(
                            tx
                          )}`}
                        >
                          {formatStatus(tx)}
                        </span>
                        <p className="text-xs text-white/60 font-medium">
                          {tx.processor || 'Stripe'}
                        </p>
                        <p className="text-xs text-white/40 mt-1">
                          Mesa {tx.numeroMesa ?? (mesaLabel || mesaId)}{' '}
                          {tx.posOrderId && (
                            <>
                              • Ticket{' '}
                              <span className="font-mono">
                                {tx.posOrderId}
                              </span>
                            </>
                          )}
                        </p>
                      </div>
                      <div className="text-right space-y-1">
                        <p className={`text-lg font-semibold ${amountColor(tx)}`}>
                          RD${formatCurrency(amount)}
                        </p>
                        {created && (
                          <p className="text-xs text-white/40">
                            {created.toLocaleDateString('es-DO', {
                              year: 'numeric',
                              month: 'short',
                              day: '2-digit',
                            })}{' '}
                            •{' '}
                            {created.toLocaleTimeString('es-DO', {
                              hour: 'numeric',
                              minute: '2-digit',
                            })}
                          </p>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
